import React, { useMemo, useRef, useState } from "react";

import { Checkbox, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { WorkspaceColumnSettingsIcon } from "assets/material-custom-icons";
import { FolderKind } from "store/types";

export interface ColumnOption {
  id: string;
  label: string;
}

export const COLUMN_OPTIONS_REGULAR: ColumnOption[] = [
  { id: "name", label: "Name" },
  { id: "deal", label: "Deal" },
  { id: "altius_file_classification", label: "Altius Classification" },
  { id: "investor", label: "Investor" },
  { id: "file_status", label: "Status" },
  { id: "tags", label: "Tags" },
  { id: "shared_with", label: "Shared With" },
  { id: "type", label: "Type" },
  { id: "last_update", label: "Last Update" },
  { id: "effective_date", label: "Effective Date" },
  { id: "currency", label: "Currency" },
  { id: "source", label: "Source" },
  { id: "upload_date", label: "Upload Date" }
];

export const COLUMN_OPTIONS_DATAROOM: ColumnOption[] = [
  { id: "dataroom_classification", label: "Cloud Classification" },
  { id: "dataroom_sync_date", label: "Cloud Sync Date" },
  { id: "dataroom_index_date", label: "Cloud Index Date" },
  { id: "dataroom_effective_date", label: "Cloud Effective Date" },
  { id: "account_name", label: "Cloud Account Name" }
];

interface Props {
  columnVisibility: Record<string, boolean>;
  onToggle: (columnId: string) => void;
  kind: FolderKind;
  showDealColumn?: boolean;
  showDataroomColumns?: boolean;
  hideShareColumn?: boolean;
}

export const ColumnVisibilityMenu: React.FC<Props> = ({
  columnVisibility,
  onToggle,
  kind,
  showDealColumn,
  showDataroomColumns,
  hideShareColumn
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const options = useMemo(() => {
    let cols: ColumnOption[];
    if (kind === "dataroom" || showDataroomColumns) {
      cols = [...COLUMN_OPTIONS_REGULAR, ...COLUMN_OPTIONS_DATAROOM];
    } else {
      cols = [...COLUMN_OPTIONS_REGULAR];
    }
    if (!showDealColumn) {
      cols = cols.filter((col) => col.id !== "deal");
    }
    if (hideShareColumn) {
      cols = cols.filter((col) => col.id !== "shared_with");
    }
    return cols;
  }, [kind, showDealColumn, showDataroomColumns, hideShareColumn]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={() => setAnchorEl(buttonRef.current)}
        sx={{ padding: "4px", ...(open && { opacity: "0.7 !important" }) }}
      >
        <WorkspaceColumnSettingsIcon sx={{ fontSize: 24, fill: "#0A3142" }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow:
                "0px 3px 3px -2px rgba(38,54,70,0.16), 0px 3px 4px 0px rgba(38,54,70,0.10), 0px 1px 8px 0px rgba(38,54,70,0.08)",
              py: "8px",
              minWidth: 220
            }
          }
        }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        {options.map((col) => (
          <MenuItem
            key={col.id}
            onClick={() => onToggle(col.id)}
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              px: "12px",
              py: "8px",
              minHeight: 40,
              "&:hover": { backgroundColor: "#ECF1F8" }
            }}
          >
            <Checkbox
              checked={columnVisibility[col.id] !== false}
              size="medium"
              disableRipple
              readOnly
              sx={{
                p: 0,
                color: "#0a3142",
                "&.Mui-checked": { color: "#0a3142" }
              }}
            />
            <Typography sx={{ fontSize: 16, color: "#071f35", lineHeight: "24px", fontWeight: 400 }}>
              {col.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
